// File: frontend/src/pages/InvestorPitch/components/slides/ClosingSlide/ClosingSlide.js

import React from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ANIMATION_VARIANTS } from '../../../constants/animations';
import styles from './ClosingSlide.module.css';

const ContactCard = ({ type, email, icon, color }) => {
  const Icon = LucideIcons[icon];
  const { t } = useLanguage();
  
  return (
    <div className={styles.contactCard}>
      <div className={styles.cardHeader}>
        <div className={styles.iconWrapper} style={{ color: color }}>
          <Icon className={styles.icon} />
        </div>
        <h4 className={styles.contactType}>
          {t(`investor_pitch.closing.contact.types.${type}`)}
        </h4>
      </div>
      <div className={styles.cardContent}>
        <a href={`mailto:${email}`} className={styles.email}>
          {email}
        </a>
      </div>
    </div>
  );
};

const ClosingSlide = () => {
  const { t } = useLanguage();

  // Translations
  const translations = {
    'investor_pitch.closing.logo.alt': 'Allegory Logo',
    'investor_pitch.closing.contact.title': 'Contact Us',
    'investor_pitch.closing.contact.types.ai_inquiries': 'AI & Technology',
    'investor_pitch.closing.contact.types.support_inquiries': 'Support & Partnership',
    'investor_pitch.closing.contact.types.press_media': 'Press & Media',
    'investor_pitch.closing.disclaimer.title': 'Legal',
    'investor_pitch.closing.disclaimer.text': `© ${new Date().getFullYear()} Allegory Insurance Technologies Inc. All rights reserved.\n\nThe information contained in this presentation is confidential and proprietary to Allegory Insurance Technologies Inc. It is provided for informational purposes only and should not be considered as an offer to sell or a solicitation of an offer to buy any securities or services. This presentation contains forward-looking statements that involve risks and uncertainties. Actual results may differ materially from those projected.`
  };

  const contactInfo = [
    {
      type: 'ai_inquiries',
      email: "ai@allegory.app",
      icon: "Brain",
      color: "var(--color-blue-310)"
    },
    {
      type: 'support_inquiries',
      email: "support@allegory.app",
      icon: "HeartHandshake",
      color: "var(--color-green-310)"
    },
    {
      type: 'press_media',
      email: "press@allegory.app",
      icon: "Newspaper",
      color: "var(--color-orange-310)"
    }
  ];

  return (
    <motion.div
      className={styles.container}
      variants={ANIMATION_VARIANTS.stagger.container}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className={styles.header}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <div className={styles.logo}>
          <img 
            src="/logos/allegory-logo-white.svg" 
            alt={t('investor_pitch.closing.logo.alt')} 
            className={styles.logoImage}
          />
        </div>
      </motion.div>

      <div className={styles.content}>
        <motion.div 
          className={styles.contactSection}
          variants={ANIMATION_VARIANTS.stagger.container}
        >
          <motion.h3 
            className={styles.sectionTitle}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            {t('investor_pitch.closing.contact.title')}
          </motion.h3>
          
          <motion.div 
            className={styles.contactGrid}
            variants={ANIMATION_VARIANTS.stagger.container}
          >
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.type}
                className={styles.contactCardWrapper}
                variants={ANIMATION_VARIANTS.stagger.item}
                custom={index}
              >
                <ContactCard 
                  type={info.type}
                  email={info.email}
                  icon={info.icon}
                  color={info.color}
                />
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div 
          className={styles.disclaimerSection}
          variants={ANIMATION_VARIANTS.fadeIn}
        >
          <h3 className={styles.sectionTitle}>
            {t('investor_pitch.closing.disclaimer.title')}
          </h3>
          <div className={styles.disclaimerCard}>
            <p className={styles.disclaimer}>
              {t('investor_pitch.closing.disclaimer.text', {
                year: new Date().getFullYear()
              })}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ClosingSlide;